/**
 * V1 capability catalog + role-default matrix — the single source of truth for
 * the Settings → Permissions verify page (and, later, the `resolveCapability`
 * engine). See docs/plans/2026-06-30_CAPABILITY_PERMISSIONS.md.
 *
 * Convention (§4b): every cap is one of three value-sets, and the value-set tells
 * you the cap KIND:
 *   - access : none | read | write   (manage_<thing> / <thing>_readwrite)
 *   - lock   : low  | mid  | top      (manage_<thing>_lock)
 *   - bool   : false | true           (allow_<thing>)
 *
 * DEFERRED (NOT in V1, keep current hardcoded role gates): teams,
 * analytics/audit (PLATFORM-ADMIN), keybook. Intentionally absent here.
 */

export type CapValueSet = 'access' | 'lock' | 'bool'

/** A concrete value a cap can hold. */
export type CapValue = 'none' | 'read' | 'write' | 'low' | 'mid' | 'top' | boolean

/** The ordered options per value-set (low→high). The order is load-bearing for
 *  the resolver's "max wins" + the UI's ordering. */
export const CAP_VALUE_OPTIONS: Record<CapValueSet, CapValue[]> = {
  access: ['none', 'read', 'write'],
  lock: ['low', 'mid', 'top'],
  bool: [false, true],
}

/** Is `value` at least `required` within its value-set? The one comparison the
 *  resolver (server) AND the client `can()` share, so it lives here in
 *  @papan/types (browser-safe — @papan/database can't run client-side).
 *  Booleans: `true` satisfies anything; `false` only satisfies `false`. */
export function capAtLeast(value: CapValue, required: CapValue, set: CapValueSet): boolean {
  if (set === 'bool') return value === true ? true : required === false
  const o = CAP_VALUE_OPTIONS[set] as CapValue[]
  const vi = o.indexOf(value)
  // An out-of-set stored/loaded value can never satisfy a requirement — fail
  // CLOSED. (Without this, a corrupt value AND an out-of-set `required` would
  // both be -1 and `-1 >= -1` would grant.) Writes validate `level`, so this is
  // defense-in-depth for mismatched-set or pre-validation rows.
  if (vi < 0) return false
  return vi >= o.indexOf(required)
}

/** Tenant role ids as stored in `memberships.role`. UI labels below. */
export type RoleId = 'owner' | 'admin' | 'member' | 'viewer' | 'guest'

export const CAP_ROLES: ReadonlyArray<{ id: RoleId; label: string; hint: string }> = [
  { id: 'owner', label: 'Owner', hint: 'Full control — always-allow, can’t be denied' },
  { id: 'admin', label: 'Drive admin', hint: 'Manage & Edit' },
  { id: 'member', label: 'Member', hint: 'Read & Write' },
  { id: 'viewer', label: 'Viewer', hint: 'Read-only' },
  { id: 'guest', label: 'Guest', hint: 'No access by default' },
]

/**
 * Every role a MEMBERSHIP or an INVITE can carry — `CAP_ROLES` plus `noaccess`.
 *
 * `noaccess` is deliberately absent from `RoleId` and `CAP_ROLES`: it holds no
 * capabilities, so it is not a column in the permission matrix. But it IS
 * assignable — the invite whitelist accepts it and the role pickers offer it —
 * which makes `CAP_ROLES` the wrong list to reach for whenever the question is
 * "what may this person be given?" rather than "what may this role do?".
 */
export const ORG_ROLE_IDS: readonly string[] = [...CAP_ROLES.map(r => r.id), 'noaccess']

/**
 * How the org roles ORDER against each other, for "may I act on this role?".
 *
 * Nobody may set access for a role above their own — an admin cannot re-permission
 * the owner role, a member cannot re-permission admins. Lives here rather than
 * inside the route that enforces it because the AGENT has to answer the same
 * question BEFORE raising an approval card: a card that names a grant the route
 * will 403 is a card the user consents to for nothing. A second copy of this
 * table in the tool is exactly how a card and its route drift apart.
 *
 * Includes `noaccess`, which `RoleId` deliberately excludes but a membership
 * can hold — so the key is `string`, and an unknown role floors at 0 rather
 * than becoming `NaN` and comparing false in every direction.
 */
export const ORG_ROLE_RANK: Readonly<Record<string, number>> = {
  noaccess: 0, guest: 1, viewer: 2, member: 3, admin: 4, owner: 5,
}

/** May `callerRole` set access FOR `targetRole`? Equal rank is allowed. */
export function roleCanTarget(callerRole: string | undefined, targetRole: string): boolean {
  return (ORG_ROLE_RANK[targetRole] ?? 0) <= (ORG_ROLE_RANK[callerRole ?? ''] ?? 0)
}

/**
 * The display name for an org role. THE one map — it was copied into four
 * places (the capability matrix, two lookups in SettingsView, and the invite
 * email), and the copies disagreed: the only one without `noaccess` was the one
 * the invitation email happened to use, so a "No Access" invite mailed the raw
 * identifier `noaccess` to the recipient.
 *
 * Derived from `CAP_ROLES` so the five capability-bearing roles can never drift
 * from the matrix; `noaccess` is the single explicit addition, for the reason
 * `ORG_ROLE_IDS` gives.
 *
 * An unknown id returns itself. Custom roles are not a thing yet, but every
 * caller here is display code — showing a raw id beats showing nothing.
 */
const ORG_ROLE_LABELS: Record<string, string> = {
  ...Object.fromEntries(CAP_ROLES.map(r => [r.id, r.label])),
  noaccess: 'No Access',
}

export function orgRoleLabel(role: string): string {
  return ORG_ROLE_LABELS[role] ?? role
}

export interface CapDef {
  id: string
  label: string
  section: string
  values: CapValueSet
  /** The resource a grant is scoped to. `tenant` = one value org-wide.
   *  `folder` = the role's DEFAULT on a folder, overridable per-folder via the
   *  folder ACL (grant scope = a folderId). `team` = per-team, overridable via
   *  team-admin authority (grant scope = a teamId). `keybook` = per keybook
   *  collection or key (grant scope = a collectionId or keyId). folder|team|
   *  keybook are all "resource-scoped" — the difference is which resource id
   *  lands in capability_grants.scope; the resolver reads it per the capability. */
  scope: 'tenant' | 'folder' | 'team' | 'keybook'
  note?: string
  /** Default value per role. Owner is always effectively max regardless. */
  defaults: Record<RoleId, CapValue>
}

const T = 'tenant' as const
const F = 'folder' as const
const TEAM = 'team' as const
const KB = 'keybook' as const

/** The V1 capability catalog with role defaults reproducing today's behavior.
 *  `allow_sharing_to_outsider` is special: the resolver reads it through to the
 *  live allow_sharing gate, whose ROLE_ALLOW_SHARING_DEFAULT (@papan/database)
 *  is DERIVED from this cap's defaults — so this matrix is the single source of
 *  truth and the two can't drift. */
export const CAPABILITIES: ReadonlyArray<CapDef> = [
  // ── Membership / Roles ────────────────────────────────────────────────
  { id: 'manage_member', label: 'Manage members', section: 'Membership / Roles', values: 'access', scope: T,
    note: 'list (read) · remove / set role (write)',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'manage_member_role', label: 'Manage member-tier roles', section: 'Membership / Roles', values: 'access', scope: T,
    note: 'set member / viewer / guest',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'manage_admin_role', label: 'Manage admin role', section: 'Membership / Roles', values: 'access', scope: T,
    note: 'promote/demote admin — owner-only today',
    defaults: { owner: 'write', admin: 'none', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'manage_owner_role', label: 'Manage owner role', section: 'Membership / Roles', values: 'access', scope: T,
    note: 'promote/demote owner — highest risk',
    defaults: { owner: 'write', admin: 'none', member: 'none', viewer: 'none', guest: 'none' } },

  // ── Invitations ───────────────────────────────────────────────────────
  { id: 'manage_invite', label: 'Manage invitations', section: 'Invitations', values: 'access', scope: T,
    note: 'invite_admin = manage_invite:write + manage_admin_role:write',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },

  // ── Member budget ─────────────────────────────────────────────────────
  { id: 'manage_budget', label: 'Manage AI budget', section: 'Member Management', values: 'access', scope: T,
    // The ONE capability of the 32 that carried no note, which made it the one
    // row on the Permissions screen showing nothing but an identifier — and it
    // is the row that opens both money pages. It also splits across two
    // screens with different names, so "what am I granting?" was unanswerable
    // from here without opening the product.
    note: 'read → Settings → Spending (what the org spent); write → Settings → Budget (the caps)',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },

  { id: 'manage_chat_storage', label: 'Manage chat storage', section: 'Member Management', values: 'access', scope: T,
    // NOT folded into `manage_budget` (#758, D13). That one's own note scopes
    // it to the two MONEY pages — Spending and Budget — and chat storage is
    // neither: it divides disk, not spend, and an org may well want the
    // person who watches the drive to manage it without handing them the
    // bill. Reusing it would have made that note false.
    //
    // Defaults reproduce the previous hardcoded owner|admin check exactly, so
    // introducing this changes no behaviour until an owner edits it here —
    // the same promise `manage_billing` below makes, for the same reason.
    note: 'read → Settings → Storage → Chat storage (who is using what); write → set a member\u2019s limit and the role defaults',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },

  // ── Billing ───────────────────────────────────────────────────────────
  { id: 'manage_billing', label: 'Manage billing', section: 'Member Management', values: 'access', scope: T,
    note: 'subscribe / change plan / cancel / portal + invoices + payment method. Defaults reproduce the previous hardcoded owner|admin check, so introducing this changed no behaviour until an owner edits it here.',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },

  // ── Org ───────────────────────────────────────────────────────────────
  { id: 'manage_org', label: 'Rename / org settings', section: 'Tenant / Org', values: 'access', scope: T,
    // Named surface by surface, because this row gates something DESTRUCTIVE
    // and the old note ("rename org + general settings") did not hint at it:
    // lowering a pattern rule deletes the file versions it no longer allows.
    // An operator answering "who may purge our version history?" has to be
    // able to find that answer here, and "general settings" is not it.
    note: 'rename org + general settings. read/write also gates Settings → Version retention, INCLUDING the pattern rules — lowering one permanently deletes the older file versions it no longer allows.',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'delete_org', label: 'Delete organization', section: 'Tenant / Org', values: 'bool', scope: T,
    note: 'the nuclear button — separate from rename, owner-only by default',
    defaults: { owner: true, admin: false, member: false, viewer: false, guest: false } },
  { id: 'purge_file', label: 'Permanently delete (purge)', section: 'Tenant / Org', values: 'bool', scope: T,
    note: 'tenant authority to hard-delete a bin item, bypassing retention (restore stays write). Gates the per-item purge + the scheduled purge-expired sweep; the per-item DELETE ALSO needs admin/owner access on the item. Default admin.',
    defaults: { owner: true, admin: true, member: false, viewer: false, guest: false } },
  { id: 'empty_bin', label: 'Empty bin', section: 'Tenant / Org', values: 'bool', scope: T,
    note: 'the tenant-wide bin nuke — hard-deletes EVERY item with NO per-item reach check, so it can\'t be reach-gated like the per-item purge. Owner-only by default; grant to admin explicitly if desired.',
    defaults: { owner: true, admin: false, member: false, viewer: false, guest: false } },
  // #1102 item 4 (owner, 2026-10-28) — the storage page's cross-folder review surfaces, which were a hardcoded
  // owner|admin check. Defaults reproduce that check, so nothing changes until an owner edits it.
  { id: 'manage_file_versions', label: 'Manage old versions and duplicates', section: 'Tenant / Org', values: 'access', scope: T,
    note: 'read → Settings → Storage\'s tenant-wide review: the old-versions list, exact duplicates, the largest folders, and every file\'s name in a storage category (these cross folder permissions); write → also purge old versions. Deleting a file still needs write on it.',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'view_file_audit', label: 'View file activity log', section: 'Tenant / Org', values: 'access', scope: T,
    note: 'read → Settings → Organization → File activity (who created / updated / moved / deleted / restored what). System-written; there is no write.',
    defaults: { owner: 'read', admin: 'read', member: 'none', viewer: 'none', guest: 'none' } },

  // ── Mini Apps ──────────────────────────────────────────────────────────
  // App-principal capability (not a human one): whether a mini app may RECEIVE
  // org/team domain events (member/role/team) — its triggers only fire on those
  // sources if granted. The envelope carries member emails / role changes, so
  // this is the "subscribe entitlement" that gates delivery at DISPATCH (the real
  // trust boundary), independent of who authored the trigger. Default DENY for
  // every role — only an explicit per-app grant (owner/admin-set) turns it on.
  { id: 'receive_org_events', label: 'Receive org & team events', section: 'Mini Apps', values: 'bool', scope: T,
    note: 'App principals only. Off by default; an owner/admin grants it per mini app. Human roles never get it (org-event delivery to people is unrelated).',
    defaults: { owner: false, admin: false, member: false, viewer: false, guest: false } },

  // ── File / Folder Permission (resource-scoped: role DEFAULT on a folder) ─
  { id: 'file_readwrite', label: 'File access', section: 'File / Folder Permission', values: 'access', scope: F,
    note: 'role’s default content access on a folder',
    defaults: { owner: 'write', admin: 'write', member: 'write', viewer: 'read', guest: 'none' } },
  { id: 'manage_folder_permission', label: 'Manage folder ACL', section: 'File / Folder Permission', values: 'access', scope: F,
    note: 'apex meta — grants file_readwrite + the sharing pair. For a folder scope, the resolver reads through to the live folder ACL (write iff admin/owner on that folder), so a member who is admin of one folder resolves write THERE. (defaults below = the tenant-wide / no-folder fallback)',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'manage_folder_permission_lock', label: 'Set protection lock', section: 'File / Folder Permission', values: 'lock', scope: F,
    note: 'default grant lock = low; who may raise it',
    defaults: { owner: 'top', admin: 'mid', member: 'low', viewer: 'low', guest: 'low' } },

  // ── Outsider Sharing ──────────────────────────────────────────────────
  // Two domains, each a {leaf, meta} pair. Leaf = the actor's own effective
  // capability (bool). Meta = manage_<leaf> (access): :read LISTS who holds the
  // leaf, :write ASSIGNS it. This is the LLM-addressable surface for an agent
  // managing outsider permissions: read to observe, write to mutate.
  //
  // Domain 1 — a MEMBER shares content OUT to an outsider:
  { id: 'allow_sharing_to_outsider', label: 'Share to outsiders', section: 'Outsider Sharing', values: 'bool', scope: F,
    note: 'LEAF (member side). SINGLE SOURCE OF TRUTH — ROLE_ALLOW_SHARING_DEFAULT derives from these. Resolver reads through to the live allow_sharing gate. viewer=false: read-only members do NOT share to outsiders by default (a tenant may opt them in); guest = no',
    defaults: { owner: true, admin: true, member: true, viewer: false, guest: false } },
  { id: 'manage_allow_sharing_to_outsider', label: 'Manage outsider-sharing', section: 'Outsider Sharing', values: 'access', scope: F,
    note: 'META of allow_sharing_to_outsider — :read lists / :write assigns whether members may share to outsiders. With no explicit grant it DERIVES from manage_folder_permission (folder admin ⇒ may manage a principal’s outsider-sharing). Standalone — also grantable to an app WITHOUT full folder ACL. (defaults below = the fallback when nothing derives)',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  // Domain 2 — an OUTSIDER reshares a received share onward:
  { id: 'reshare_as_outsider', label: 'Reshare as an outsider', section: 'Outsider Sharing', values: 'bool', scope: F,
    note: 'LEAF (outsider side). The recipient’s effective "may I reshare this onward" — grant-driven, stored as share_grants.allow_sharing, SET by manage_reshare_as_outsider. Role defaults are false; the live value comes from the share grant. RESERVED: enforcement today reads share_grants.allow_sharing directly; the resolver read-through (needs a share scope) is pending.',
    defaults: { owner: false, admin: false, member: false, viewer: false, guest: false } },
  { id: 'manage_reshare_as_outsider', label: 'Let outsiders re-share', section: 'Outsider Sharing', values: 'access', scope: F,
    note: 'META of reshare_as_outsider — :read lists / :write assigns whether outsiders may reshare. V1: explicit grant wins (e.g. a narrowly scoped app), else MIRRORS allow_sharing_to_outsider — write when the principal may share out, none otherwise (you can only let an outsider reshare what you could share in the first place). :write gates writing share_grants.allow_sharing=true. (defaults below = unreachable fallback — resolve always returns the grant or the mirror; kept equal to the mirror of allow_sharing_to_outsider so the Settings matrix display can’t contradict resolution.)',
    defaults: { owner: 'write', admin: 'write', member: 'write', viewer: 'none', guest: 'none' } },

  // ── Cross-Org Connections ─────────────────────────────────────────────
  { id: 'manage_connection', label: 'Manage connections', section: 'Cross-Org Connections', values: 'access', scope: T,
    note: 'approve/reject/revoke · initiate=allow_sharing_to_outsider · reshare=manage_reshare_as_outsider',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },

  // ── Teams (team-scoped: grant scope = a teamId; authority splits by TARGET —
  //    your own team vs the teams below you. Derived from isTeamAdminRecursive
  //    the way folder caps derive from the folder ACL.) ────────────────────
  // #1102 item 1 — the tenant-wide team authority that used to be a hardcoded owner|admin check. Defaults reproduce
  // that check exactly, so introducing it changes nothing until an owner edits it (the `manage_billing` promise). The
  // team-scoped caps below (manage_subteam, manage_team_member, manage_team_admin, rename_team) are unchanged.
  { id: 'manage_team', label: 'Manage all teams', section: 'Teams', values: 'access', scope: T,
    note: 'read → see every team (not only your own, their ancestors and descendants), grant a folder to any team, and view a team\u2019s own permission grants; write → also create a top-level team (or move one to the top level) and set a team\u2019s permission grants. Managing a team itself (sub-teams, members, team admins, rename) is the team caps below — a top-level team you create is managed through them, so grant those too.',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'rename_team', label: 'Rename own team', section: 'Teams', values: 'bool', scope: TEAM,
    note: 'rename THIS team (its own name). OFF by default — renaming a team is the parent’s authority (via manage_subteam over it); grant this to delegate self-rename to a team’s own admin. Owner/admin default true (org-level authority); a team admin gets it only when handed over.',
    defaults: { owner: true, admin: true, member: false, viewer: false, guest: false } },
  { id: 'manage_subteam', label: 'Manage sub-teams', section: 'Teams', values: 'access', scope: TEAM,
    note: 'authority over this team’s CHILDREN — create / delete / rename / reorder / re-parent sub-teams. :read sees the sub-tree, :write mutates it. Read-through: admin of this team or an ancestor. (Renaming a CHILD is here; renaming THIS team is rename_team.)',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'manage_team_member', label: 'Manage team members', section: 'Teams', values: 'access', scope: TEAM,
    note: 'this team’s membership — :read lists members, :write adds / removes them. Read-through: admin of this team or an ancestor. (Assigning team-admin is the separate manage_team_admin.)',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'manage_team_admin', label: 'Assign team admins', section: 'Teams', values: 'access', scope: TEAM,
    note: 'assign / revoke the team-admin role on this team — the ESCALATION gate (a team admin minting more team admins), split from manage_team_member so it locks on its own. :read lists team admins, :write mutates. Read-through: admin of this team or an ancestor.',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'manage_team_permission_lock', label: 'Set team protection lock', section: 'Teams', values: 'lock', scope: TEAM,
    note: 'who may RAISE the low/mid/top protection lock on a team-cap grant — mirrors manage_folder_permission_lock. The lock is the orthogonal protection axis (the team hierarchy already ranks structural authority); a grant locked at a tier resists a lower-tier admin overriding it.',
    defaults: { owner: 'top', admin: 'mid', member: 'low', viewer: 'low', guest: 'low' } },

  // ── Keybook. ACCESS to keys is the keybook's own grant hierarchy (root →
  //    collection → key, migration 0274 — features/keybook/core/resolve-access.ts),
  //    not capabilities: `manage_keybook_collection_permission` and
  //    `keybook_readwrite` stay retired. `manage_keybook_permission` is BACK with a
  //    NEW meaning (2026-10-17; its pre-0274 rows were translated by 0274 and
  //    deleted by 0279, so none can come back with the old one) — modelled on
  //    manage_folder_permission. ────────────────────────────────────────────────
  { id: 'manage_keybook_permission', label: 'Manage keybook access', section: 'Keybook', values: 'access', scope: T,
    note: 'read → may SEE the access list (read-only: "No access" rows hidden, no manager-only fields) and the Exposure list of every key and collection the holder can already see — never a key or collection they can\'t see, never a value. write → as for folders, but NOT authority on its own: changing who has access still needs Manage on that key / collection, through the judge. Write (or Manage) on an item counts as read FOR THAT ITEM.',
    defaults: { owner: 'write', admin: 'write', member: 'none', viewer: 'none', guest: 'none' } },

  // ── Keybook Outsider Sharing (SEPARATE, owner-tight, PURE-GRANT — no
  //    derivation from keybook access; the owner is the sole root and
  //    delegates explicitly, bounded by the grant lock). ─────────────────────
  { id: 'allow_sharing_keybook_to_outsider', label: 'Share keybook to outsiders', section: 'Keybook Outsider Sharing', values: 'bool', scope: KB,
    note: 'LEAF. May this principal share a key OUT to an outsider. Owner-tight: false for everyone (incl. admins) unless explicitly granted — collection-admin/creator authority does NOT extend here. No read-through (pure grant).',
    defaults: { owner: true, admin: false, member: false, viewer: false, guest: false } },
  { id: 'manage_allow_sharing_keybook_to_outsider', label: 'Manage keybook outsider-sharing', section: 'Keybook Outsider Sharing', values: 'access', scope: KB,
    note: 'META of allow_sharing_keybook_to_outsider — who may grant it. NO derivation (unlike the folder meta): explicit, owner-rooted. Owner grants it to a collection admin / creator / any principal, who (bounded by the lock) may grant the leaf onward.',
    defaults: { owner: 'write', admin: 'none', member: 'none', viewer: 'none', guest: 'none' } },
  { id: 'reshare_keybook_as_outsider', label: 'Reshare keybook as an outsider', section: 'Keybook Outsider Sharing', values: 'bool', scope: KB,
    note: 'LEAF (outsider side). The recipient’s effective "may I reshare this key onward". Grant-driven, owner-tight. No read-through (pure grant); resolves before the owner floor (a recipient owner does not auto-reshare) — role defaults all false, like reshare_as_outsider.',
    defaults: { owner: false, admin: false, member: false, viewer: false, guest: false } },
  { id: 'manage_reshare_keybook_as_outsider', label: 'Let outsiders reshare keybook', section: 'Keybook Outsider Sharing', values: 'access', scope: KB,
    note: 'META of reshare_keybook_as_outsider — who may grant it. Explicit, owner-rooted, no derivation. Owner delegates, bounded by the lock.',
    defaults: { owner: 'write', admin: 'none', member: 'none', viewer: 'none', guest: 'none' } },
]

/** Section order for the verify page. */
export const CAP_SECTIONS: ReadonlyArray<string> = [
  'Membership / Roles', 'Invitations', 'Member Management', 'Tenant / Org',
  'File / Folder Permission', 'Outsider Sharing', 'Cross-Org Connections', 'Teams',
  'Keybook', 'Keybook Outsider Sharing',
]

/** Pretty-print a value for the UI. */
export function capValueLabel(v: CapValue): string {
  if (v === true) return 'yes'
  if (v === false) return 'no'
  return v
}

/** The value-set of a capability by id — lets the client `can()` compare a
 *  resolved value without hardcoding which kind each cap is. */
const CAP_SET_BY_ID: ReadonlyMap<string, CapValueSet> = new Map(CAPABILITIES.map((c) => [c.id, c.values]))
/** A Map, never a plain object: `constructor` / `__proto__` / `toString` are not capabilities (#1102). */
export function capSetOf(capId: string): CapValueSet | undefined {
  return CAP_SET_BY_ID.get(capId)
}
